export interface SubstringMatch {
  offset: number;
  length: number;
}

export interface Prediction<T = any> {
  value: T;
  matches: SubstringMatch[];
  subMatches: SubstringMatch[];
  mainParts: { text: string; match?: boolean }[];
  subParts: { text: string; match?: boolean }[];
  label: string;
  subLabel: string;
  icon?: string;
}

export function parseSubStringMatches(text: string, matches: SubstringMatch[] = []) {
  const parts: { text: string; match?: boolean }[] = [];
  if (!matches.length) {
    parts.push({ text });
    return parts;
  }
  let index = 0;
  for (const match of matches) {
    if (index < match.offset) {
      parts.push({ text: text.substring(index, match.offset) });
    }
    index = match.offset + match.length;
    parts.push({ text: text.substring(match.offset, index), match: true });
  }
  if (index < text.length) {
    parts.push({ text: text.substring(index, text.length) });
  }
  return parts;
}

export function formatPredictions(
  fetchedPredictions: any[],
  searchText: string,
  opts?: { labelKey?: string; secondaryLabelKey?: string; skipSecondaryLabelMatch?: boolean }
): Prediction[] {
  const predictions: Prediction[] = [];
  const words = searchText?.match(/\b\w+\b/g) || [];
  let reg: RegExp;
  if (words?.length > 1) {
    let regStr = '';
    words.forEach((w, i) => {
      regStr += `\\b${w}\\b`;
      if (i < words.length - 1) {
        regStr += '|';
      }
    });
    reg = new RegExp(regStr, 'gi');
  } else {
    reg = new RegExp(searchText, 'gi');
  }
  fetchedPredictions.forEach((o) => {
    if (typeof o === 'string') {
      const matches = [...o.matchAll(reg)];
      const p: Prediction = {
        value: o,
        label: o,
        subLabel: '',
        matches: [],
        mainParts: [],
        subMatches: [],
        subParts: [],
      };
      if (matches.length) {
        matches.forEach((match) => {
          p.matches.push({
            offset: match?.index || 0,
            length: match[0].length || 0,
          });
        });
        p.mainParts = parseSubStringMatches(o, p.matches);
      }
      predictions.push(p);
    } else if (typeof o === 'object' && !!o && opts?.labelKey) {
      const text = o[opts.labelKey] as string;
      const subText = opts.secondaryLabelKey ? (o[opts.secondaryLabelKey] as string) || '' : '';
      const mainMatches = [...text.matchAll(reg)];
      const p: Prediction = {
        value: o,
        label: text,
        subLabel: subText,
        matches: [],
        mainParts: text ? [{ match: false, text }] : [],
        subMatches: [],
        subParts: subText ? [{ match: false, text: subText }] : [],
        icon: o.icon,
      };
      if (mainMatches.length) {
        mainMatches.forEach((match) => {
          p.matches.push({
            offset: match.index || 0,
            length: match[0].length || 0,
          });
        });
        p.mainParts = parseSubStringMatches(text, p.matches);
      }
      if (!opts.skipSecondaryLabelMatch) {
        const subMatches = [...subText.matchAll(reg)];
        if (subMatches.length) {
          subMatches.forEach((match) => {
            p.subMatches.push({
              offset: match.index || 0,
              length: match[0].length || 0,
            });
          });
          p.subParts = parseSubStringMatches(subText, p.subMatches);
        }
      }
      predictions.push(p);
    }
  });
  return predictions;
}
